import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EMPTY } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { DataService } from './data.service';
import { AuthService, DataScope } from './auth.service';
import { MonthlyReport } from '../models/report.model';
import {InvoiceReportCSVResponse} from '../models/processedVin/invoiceReportCSVResponse.model';
import { environment } from '../../environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { LoaderDiagComponent } from '../shared/loader-diag/loader-diag.component';
import { ProcessedVinsPaginatedResponse } from '../models/processedVin/processedVinsPaginatedResponse.model';
import {UnprocessedVinPaginatedResponseModel} from '../models/unprocessedVin/unprocessedVinPaginatedResponse.model';
import {UnprocessedSoldVinPaginatedResponseModel} from '../models/soldUnprocessed/unprocessedSoldVinPaginatedResponse.model';
import {UnprocessedVinsReportCSVResponse} from '../models/unprocessedVin/unprocessedVinsReportCSVResponse.model';
import {UnprocessedSoldVinsReportCSVResponse} from '../models/soldUnprocessed/unprocessedSoldVinsReportCSVResponse.model';
import { ActivityByDateChartResponse } from '../models/processedVin/activityByDateChartResponse.model';
import { SoldUnprocessedCountsResponse } from '../models/landingPage/soldUnprocessedCountsResponse.model';
import { UnprocessedVsProcessedCountsResponse } from '../models/landingPage/unprocessedVsProcessedCountsResponse.model';
import { FilterPrefetchResponse } from '../models/dataFilter/filterPrefetchResponse.model';
import { DataFilterService } from './data-filter.service';
import { CertificateEmailResponse, CertificateRequest, EMPTY_VIN_LOOKUP, VinLookupResponse } from '../models/vinLookup/vinLookupResponse.model';
import { ManagedUsersResponse } from '../models/userManagement/managedUsersResponse.model';

/**
 * Retrieves data from the reporting API and hands it to the DataService.
 *
 * Components should not call HttpClient directly — they trigger loads
 * through this service and read the results from DataService.
 */
@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly data = inject(DataService);
  private readonly auth = inject(AuthService);
  private readonly dialog = inject(MatDialog);
  private readonly filters = inject(DataFilterService);

  /** Base URL for the reporting API. Replace with the real endpoint. */
  private readonly baseUrl = '/api';
  private readonly envBaseUrl = environment.baseURL;

  /**
   * Load scan records for the given date range and push them into the
   * DataService.
   *
   * Currently sourced from the MockServerService; swap the body for an
   * `http.get<Scan[]>(`${baseUrl}/scans`, { params: { start, end } })`
   * call when the real endpoint is available — the rest of the app is
   * unaffected.
   */
  loadScans(start: Date, end: Date): void {
    this.data.setLoading(true);
    this.data.setError(null);
    try {
      // Scope to the signed-in user's processor (tenant), then enforce their
      // role's data scope (countries/states/sites) as a hard floor.
      const processor = this.auth.processor();
     /*  this.data.setScans(this.applyScope(scans, this.auth.scope())); */
      this.data.setDateRange({ start, end });
    } catch (err) {
      this.data.setError(err instanceof Error ? err.message : 'Failed to load scans');
    } finally {
      this.data.setLoading(false);
    }
  }

  /** Restrict scans to the user's data scope (empty dimensions = unrestricted). */
  /* private applyScope(scans: Scan[], scope: DataScope): Scan[] {
    const countries = scope.countries ?? [];
    const states = scope.states ?? [];
    const sites = scope.sites ?? [];
    if (!countries.length && !states.length && !sites.length) {
      return scans;
    }
    return scans.filter(s =>
      (!countries.length || countries.includes(s.siteAndLocationAttributes.country)) &&
      (!states.length || states.includes(s.siteAndLocationAttributes.state)) &&
      (!sites.length || sites.includes(s.siteAndLocationAttributes.userSiteName))
    );
  } */

  /**
   * Fetch monthly reporting activity and push it into the DataService.
   * Loading and error state are reflected on the DataService too.
   */
  loadMonthlyReports(): void {
    this.data.setLoading(true);
    this.data.setError(null);

    this.http
      .get<MonthlyReport[]>(`${this.baseUrl}/reports/monthly`)
      .pipe(
        tap(reports => this.data.setMonthlyReports(reports)),
        catchError(err => {
          this.data.setError(err?.message ?? 'Failed to load monthly reports');
          return EMPTY;
        }),
        finalize(() => this.data.setLoading(false))
      )
      .subscribe();
  }

  //processed vin paginated api
  getProccessedVinsPaginated(startDate: string, endDate: string, timezoneOffset: string, cursor: string|null, limit: number, useSelectedFilters: boolean) {

    let payload = {
      dateRangeDTO: {
        startDate: startDate,
        endDate: endDate,
        offset: timezoneOffset
        },
      cursor: cursor,
      limit: limit,
      filters: this.filters.requestFilters(useSelectedFilters)
    }

    this.data.setLoading(true);
    this.data.setError(null);

    /* let loadingDialog = this.dialog.open(LoaderDiagComponent, {
      disableClose: true
    }); */
    return this.http
      .post<ProcessedVinsPaginatedResponse>(this.envBaseUrl + environment.processedVinReportTablePath, payload)
      .pipe(
        tap(response => {

          if (response.data) {

            this.data.setNewScans(
              response.data.data,
              response.data.hasNext,
              response.data.hasNext ? response.data.nextCursor : null,
              response.data.hasPrevious,
              response.data.hasPrevious ? response.data.previousCursor : null,
              response.data.totalCount,
              limit
            );

            if (!environment.production) {
              console.log('Proc VIN Paginated API Response Data:')
              console.log(response.data);
            }
          }

        }),
        catchError(err => {
          this.data.setError(err?.message ?? 'Failed to Load Scan Data');
          return EMPTY;
        }),
        finalize(() => {
          this.data.setLoading(false);
          /* loadingDialog.close(); */
        })
      );
  }

  //processed vin chart api
  getProccessedVinsChart(startDate: string, endDate: string, timezoneOffset: string, useSelectedFilters: boolean) {

    let payload = {
      dateRangeDTO: {
        startDate: startDate,
        endDate: endDate,
        offset: timezoneOffset
      },
      filters: this.filters.requestFilters(useSelectedFilters)
    }

    this.data.setLoading(true);
    this.data.setError(null);

    /* let loadingDialog = this.dialog.open(LoaderDiagComponent, {
      disableClose: true
    }); */
    return this.http
      .post<ActivityByDateChartResponse>(this.envBaseUrl + environment.processedVinReportChartPath, payload)
      .pipe(
        tap(response => {

          if (response.data) {

            if (!environment.production) {
              console.log('Proc VIN Chart API Response Data:');
              console.log(response.data);
            }

            this.data.prepareChartData(response);

          }

        }),
        catchError(err => {
          this.data.setError(err?.message ?? 'Failed to Load Scan Data');
          return EMPTY;
        }),
        finalize(() => {
          this.data.setLoading(false);
          /* loadingDialog.close(); */
        })
      );
  }

  //processed vin invoice csv export api
  exportInvoiceReportCSV(startDate: string, endDate: string, timezoneOffset: string, useSelectedFilters: boolean): void {
    this.data.setLoading(true);
    this.data.setError(null);

    let loadingDialog = this.dialog.open(LoaderDiagComponent, {
      disableClose: true
    });

    this.http
      .post<InvoiceReportCSVResponse>(this.envBaseUrl + environment.invoiceReportCSVExportPath, {
        dateRangeDTO: {
          startDate: startDate,
          endDate: endDate,
          offset: timezoneOffset
        },
        filters: this.filters.requestFilters(useSelectedFilters)
      })
      .pipe(
        tap(response => {
          window.open(response.data.downloadUrl);
          /* this.data.downloadFile(response.data.downloadUrl).subscribe(); */
        }),
        catchError(err => {
          this.data.setError(err?.message ?? 'Failed to export Invoicing Report CSV');
          return EMPTY;
        }),
        finalize(() => {
          this.data.setLoading(false);
          loadingDialog.close();
        })
      )
      .subscribe();
  }

  // unprocessed Vin API functions start
  getUnProcessedVinsPaginated(startDate: string, endDate: string, timezoneOffset: string, cursor: string|null, limit: number, useSelectedFilters: boolean) {

    let payload = {
      dateRangeDTO: {
        startDate: startDate,
        endDate: endDate,
        offset: timezoneOffset
      },
      cursor: cursor,
      limit: limit,
      filters: this.filters.requestFilters(useSelectedFilters)
    }

    this.data.setLoading(true);
    this.data.setError(null);

    return this.http
      .post<UnprocessedVinPaginatedResponseModel>(this.envBaseUrl + environment.unprocessedVinReportTablePath, payload)
      .pipe(
        tap(response => {

          if (response.data) {
            this.data.setNewUnprocessedVin(
              response.data.data,
              response.data.hasNext,
              response.data.hasNext ? response.data.nextCursor : null,
              response.data.hasPrevious,
              response.data.hasPrevious ? response.data.previousCursor : null,
              response.data.totalCount,
              limit
            );
          }

        }),
        catchError(err => {
          this.data.setError(err?.message ?? 'Failed to Load Scan Data');
          return EMPTY;
        }),
        finalize(() => {
          this.data.setLoading(false);
        })
      );
  }

  exportUnprocessedVinReportCSV(startDate: string, endDate: string, timezoneOffset: string, useSelectedFilters: boolean): void {
    this.data.setLoading(true);
    this.data.setError(null);

    let loadingDialog = this.dialog.open(LoaderDiagComponent, {
      disableClose: true
    });

    this.http
      .post<UnprocessedVinsReportCSVResponse>(this.envBaseUrl + environment.unprocessedVinReportCSVExportPath, {
        dateRangeDTO: {
          startDate: startDate,
          endDate: endDate,
          offset: timezoneOffset
        },
        filters: this.filters.requestFilters(useSelectedFilters)
      })
      .pipe(
        tap(response => {
          window.open(response.data.downloadUrl);
        }),
        catchError(err => {
          this.data.setError(err?.message ?? 'Failed to export Invoicing Report CSV');
          return EMPTY;
        }),
        finalize(() => {
          this.data.setLoading(false);
          loadingDialog.close();
        })
      )
      .subscribe();
  }
  // unprocessed Vin API functions end

  // unprocessed Sold Vin API functions start
  exportUnprocessedSoldVinReportCSV(startDate: string, endDate: string, timezoneOffset: string, useSelectedFilters: boolean): void {
    this.data.setLoading(true);
    this.data.setError(null);

    let loadingDialog = this.dialog.open(LoaderDiagComponent, {
      disableClose: true
    });

    this.http
      .post<UnprocessedSoldVinsReportCSVResponse>(this.envBaseUrl + environment.unprocessedVinSoldReportCSVExportPath, {
        dateRangeDTO: {
          startDate: startDate,
          endDate: endDate,
          offset: timezoneOffset
        },
        filters: this.filters.requestFilters(useSelectedFilters)
      })
      .pipe(
        tap(response => {
          window.open(response.data.downloadUrl);
        }),
        catchError(err => {
          this.data.setError(err?.message ?? 'Failed to export Invoicing Report CSV');
          return EMPTY;
        }),
        finalize(() => {
          this.data.setLoading(false);
          loadingDialog.close();
        })
      )
      .subscribe();
  }

  getSoldUnProcessedVinsPaginated(startDate: string, endDate: string, timezoneOffset: string, cursor: string|null, limit: number, useSelectedFilters: boolean) {

    let payload = {
      dateRangeDTO: {
        startDate: startDate,
        endDate: endDate,
        offset: timezoneOffset
      },
      cursor: cursor,
      limit: limit,
      filters: this.filters.requestFilters(useSelectedFilters)
    }

    this.data.setLoading(true);
    this.data.setError(null);

    return this.http
      .post<UnprocessedSoldVinPaginatedResponseModel>(this.envBaseUrl + environment.unprocessedSoldVinReportTablePath, payload)
      .pipe(
        tap(response => {

          if (response.data) {
            this.data.setNewSoldUnprocessedVin(
              response.data.data,
              response.data.hasNext,
              response.data.hasNext ? response.data.nextCursor : null,
              response.data.hasPrevious,
              response.data.hasPrevious ? response.data.previousCursor : null,
              response.data.totalCount,
              limit
            );
          }

        }),
        catchError(err => {
          this.data.setError(err?.message ?? 'Failed to Load Scan Data');
          return EMPTY;
        }),
        finalize(() => {
          this.data.setLoading(false);
        })
      );
  }
  // unprocessed Sold Vin API functions end

  getSoldUnprocessedCounts(timezoneOffset: string) {

    if (!environment.production) {
      console.log('sold UP counts triggered');
    }

    this.data.setLoading(true);
    this.data.setError(null);

    return this.http
      .get<SoldUnprocessedCountsResponse>(this.envBaseUrl + environment.unprocessedSoldCountsPath, { params: { offset: timezoneOffset } })
      .pipe(
        tap(response => {

          if (response.data && !environment.production) {
            console.log('sold UP counts data:');
            console.log(response.data);
          }

          this.data.setSoldUnprocessedCounts(response.data);

        }),
        catchError(err => {
          this.data.setError(err?.message ?? 'Failed to Load Scan Data');
          return EMPTY;
        }),
        finalize(() => {
          this.data.setLoading(false);
        })
      );
  }

  /**
   * The user's data-filter options, pushed into DataFilterService. Held in
   * memory only, so the shell calls this on every authenticated load — a fresh
   * sign-in or a page refresh.
   */
  loadFilterOptions() {
    return this.http
      .get<FilterPrefetchResponse>(this.envBaseUrl + environment.filterPrefetchPath)
      .pipe(
        tap(response => {
          if (response.data) {
            if (!environment.production) {
              console.log('Filter Prefetch :');
              console.log(response.data);
            }
            this.filters.setCatalog(response.data);
          }
        }),
        // Without options the filter dialog simply has nothing to offer; the
        // reports still load, scoped server-side.
        catchError(() => EMPTY)
      );
  }

  getUnprocessedVsProcessedCounts(timezoneOffset: string) {

    if (!environment.production) {
      console.log('UP vs P counts triggered');
    }

    this.data.setLoading(true);
    this.data.setError(null);

    return this.http
      .get<UnprocessedVsProcessedCountsResponse>(this.envBaseUrl + environment.unprocessedVsProcessedCountsPath, { params: { offset: timezoneOffset } })
      .pipe(
        tap(response => {

          if (response.data && !environment.production) {
            console.log('P vs UP counts data:');
            console.log(response.data);
          }

          this.data.setUnprocessedVsProcessedCounts(response.data);

        }),
        catchError(err => {
          this.data.setError(err?.message ?? 'Failed to Load Scan Data');
          return EMPTY;
        }),
        finalize(() => {
          this.data.setLoading(false);
        })
      );
  }

  /**
   * Processed scans for the given full and partial VINs in the date range, with
   * the values that matched nothing. Not data-filtered: the VINs are the
   * filter. Results go back to the caller rather than into DataService: they
   * belong to the lookup page alone.
   */
  getVinLookup(vins: string[], partialVins: string[], startDate: string, endDate: string, timezoneOffset: string) {

    let payload = {
      dateRangeDTO: {
        startDate: startDate,
        endDate: endDate,
        offset: timezoneOffset
      },
      vins: vins,
      partialVins: partialVins
    }

    return this.http
      .post<VinLookupResponse>(this.envBaseUrl + environment.vinLookupPath, payload)
      .pipe(
        map(response => {

          if (response.data && !environment.production) {
            console.log('VIN Lookup API Response Data:');
            console.log(response.data);
          }

          return response.data ?? EMPTY_VIN_LOOKUP;

        }),
        catchError(err => {
          this.data.setError(err?.message ?? 'Failed to look up VINs');
          return EMPTY;
        })
      );
  }

  /**
   * Emails several certificates at once to the signed-in user — the bulk
   * path; single certificates open straight from their link. The recipient
   * comes from the session on the server, never from this request. The whole
   * response goes back: the page shows its message.
   */
  emailCertificates(certificates: CertificateRequest[], startDate: string, endDate: string, timezoneOffset: string) {

    let payload = {
      dateRangeDTO: {
        startDate: startDate,
        endDate: endDate,
        offset: timezoneOffset
      },
      certificates: certificates
    }

    return this.http
      .post<CertificateEmailResponse>(this.envBaseUrl + environment.certificateEmailPath, payload)
      .pipe(
        catchError(err => {
          // The interceptor has already shown the error snackbar.
          this.data.setError(err?.message ?? 'Failed to email certificates');
          return EMPTY;
        })
      );
  }
  /**
   * Users the signed-in account may see: its own business's, or every
   * business's for a root-level scope. The server scopes the list from the
   * token — nothing in the request names a business.
   */
  getManagedUsers() {
    return this.http
      .get<ManagedUsersResponse>(this.envBaseUrl + environment.userManagementPath)
      .pipe(
        map(response => response.data ?? []),
        catchError(err => {
          this.data.setError(err?.message ?? 'Failed to load users');
          return EMPTY;
        })
      );
  }

}
